<template>
  <div
    class="attribute-radar-chart"
    :style="`width: ${size}px; height: ${size}px;`"
  >
    <canvas ref="radarCanvas" :width="size" :height="size"></canvas>
    <div v-if="showLegend" class="radar-legend">
      <div v-for="(item, index) in chartData" :key="index" class="legend-item">
        <span
          class="legend-color"
          :style="`background-color: ${item.color}`"
        ></span>
        <span class="legend-label">{{ item.label }}</span>
        <span class="legend-value">{{ item.value }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AttributeRadarChart",
  props: {
    player: {
      type: Object,
      required: true,
    },
    size: {
      type: Number,
      default: 150,
    },
    showLegend: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    chartData() {
      const attributes = this.player.attributes || {};
      return [
        {
          label: "ATT",
          fullLabel: "Attacking",
          value: attributes.attacking || 50,
          color: "#e74c3c",
        },
        {
          label: "TEC",
          fullLabel: "Technical",
          value: attributes.technical || 50,
          color: "#3498db",
        },
        {
          label: "TAC",
          fullLabel: "Tactical",
          value: attributes.tactical || 50,
          color: "#9b59b6",
        },
        {
          label: "DEF",
          fullLabel: "Defending",
          value: attributes.defending || 50,
          color: "#27ae60",
        },
        {
          label: "CRE",
          fullLabel: "Creativity",
          value: attributes.creativity || 50,
          color: "#f39c12",
        },
      ];
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.drawRadarChart();
    });
  },
  watch: {
    player: {
      handler() {
        this.$nextTick(() => {
          this.drawRadarChart();
        });
      },
      deep: true,
    },
    size() {
      this.$nextTick(() => {
        this.drawRadarChart();
      });
    },
  },
  methods: {
    drawRadarChart() {
      const canvas = this.$refs.radarCanvas;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      const centerX = this.size / 2;
      const centerY = this.size / 2;
      const radius = this.size / 2 - 30; // More padding for labels

      // Clear canvas
      ctx.clearRect(0, 0, this.size, this.size);

      const data = this.chartData;
      const angleStep = (Math.PI * 2) / data.length;

      // Draw background circles
      ctx.strokeStyle = "#ecf0f1";
      ctx.lineWidth = 1;
      for (let i = 1; i <= 5; i++) {
        ctx.beginPath();
        ctx.arc(centerX, centerY, (radius / 5) * i, 0, Math.PI * 2);
        ctx.stroke();

        // Draw value labels on circles
        if (i === 5) {
          ctx.fillStyle = "#95a5a6";
          ctx.font = "8px Arial";
          ctx.textAlign = "center";
          const labelRadius = (radius / 5) * i;
          ctx.fillText("100", centerX, centerY - labelRadius + 3);
          ctx.fillText("80", centerX, centerY - labelRadius * 0.8 + 3);
          ctx.fillText("60", centerX, centerY - labelRadius * 0.6 + 3);
          ctx.fillText("40", centerX, centerY - labelRadius * 0.4 + 3);
          ctx.fillText("20", centerX, centerY - labelRadius * 0.2 + 3);
        }
      }

      // Draw axes
      ctx.strokeStyle = "#bdc3c7";
      ctx.lineWidth = 1;
      data.forEach((item, index) => {
        const angle = angleStep * index - Math.PI / 2;
        const x = centerX + Math.cos(angle) * radius;
        const y = centerY + Math.sin(angle) * radius;

        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(x, y);
        ctx.stroke();

        // Draw labels
        ctx.fillStyle = "#2c3e50";
        ctx.font = "bold 11px Arial";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        const labelX = centerX + Math.cos(angle) * (radius + 15);
        const labelY = centerY + Math.sin(angle) * (radius + 15);

        ctx.fillText(item.label, labelX, labelY);

        // Draw value next to label
        ctx.font = "9px Arial";
        ctx.fillStyle = "#7f8c8d";
        ctx.fillText(`(${item.value})`, labelX, labelY + 12);
      });

      // Draw data polygon
      ctx.beginPath();
      data.forEach((item, index) => {
        const angle = angleStep * index - Math.PI / 2;
        const value = Math.max(0, Math.min(100, item.value)) / 100;
        const x = centerX + Math.cos(angle) * radius * value;
        const y = centerY + Math.sin(angle) * radius * value;

        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.closePath();

      // Fill and stroke the polygon
      ctx.fillStyle = "rgba(52, 152, 219, 0.2)";
      ctx.fill();
      ctx.strokeStyle = "#3498db";
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw data points
      data.forEach((item, index) => {
        const angle = angleStep * index - Math.PI / 2;
        const value = Math.max(0, Math.min(100, item.value)) / 100;
        const x = centerX + Math.cos(angle) * radius * value;
        const y = centerY + Math.sin(angle) * radius * value;

        ctx.beginPath();
        ctx.arc(x, y, 4, 0, Math.PI * 2);
        ctx.fillStyle = item.color;
        ctx.fill();
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 2;
        ctx.stroke();
      });
    },
  },
};
</script>

<style scoped>
.attribute-radar-chart {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
}

.radar-legend {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.8rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  flex-shrink: 0;
}

.legend-label {
  font-weight: 500;
  color: #2c3e50;
  min-width: 60px;
}

.legend-value {
  font-weight: bold;
  color: #7f8c8d;
  margin-left: auto;
}

/* Dark theme support */
.dark-theme .legend-label {
  color: #ecf0f1;
}

.dark-theme .legend-value {
  color: #bdc3c7;
}
</style>
